import { useState } from "react";
import ShareSkill from "@/components/ShareSkill";
import { generateShareContent, getShareDraft } from "@/lib/api";
import type { SkillDetail, Team } from "@/lib/types";

type Mode = "manual" | "generate";
type ActiveView = "linkedin" | "x";
type ShareTab = "people" | "social";
type Draft = { x: string; linkedin: string };

const X_LIMIT = 280;

// Design-system "eyebrow" label: Space Mono, uppercase, small orange dash prefix.
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-text/45">
      <span className="h-px w-5 bg-primary" />
      {children}
    </span>
  );
}

const PLATFORM_META = {
  x: {
    label: "X (Twitter)",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.258 5.632 5.907-5.632Zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
    shareUrl: (text: string) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
  },
  linkedin: {
    label: "LinkedIn",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
    shareUrl: (url: string) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
};

// Default look for the trigger when a caller doesn't dictate one. Callers that sit this button in a
// row with sibling actions should pass `triggerClassName` so all of them match — see the rail in
// pages/Skill.tsx, where Share previously came out brand-orange next to neutral siblings.
const DEFAULT_TRIGGER_CLS =
  "inline-flex items-center gap-2 rounded-lg border px-4 py-2 font-display text-sm font-medium transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]";

export default function SocialShareModal({
  skill,
  slug,
  myTeams = [],
  accessToken,
  isOwner,
  triggerClassName,
}: {
  skill: SkillDetail;
  slug: string;
  myTeams?: Team[];
  accessToken?: string | null;
  isOwner?: boolean;
  triggerClassName?: string;
}) {
  const canPeople = !!isOwner;
  const canSocial = skill.status === "active";

  // AI generation makes a real, billed LLM call server-side — restricted to signed-in
  // users there too (this is just the matching UI: no anonymous "Generate with AI").
  const canGenerate = !!accessToken;

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<ShareTab>(canPeople ? "people" : "social");
  const [socialLoaded, setSocialLoaded] = useState(false);
  const [mode, setMode] = useState<Mode>(canGenerate ? "generate" : "manual");
  // Kept as separate slots so switching between "Generate with AI" and "Write my
  // own" never shows one mode's content mislabeled as the other's.
  const [aiDraft, setAiDraft] = useState<Draft | null>(null);
  const [manualDraft, setManualDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [customText, setCustomText] = useState("");

  const [skillUrl, setSkillUrl] = useState("");
  const [activeView, setActiveView] = useState<ActiveView>("linkedin");

  // The URL is fully derivable client-side — no need to wait on a draft response to show it.
  // `skillUrl` (from the backend) is preferred once available in case origins ever differ.
  const displayUrl = skillUrl || `${window.location.origin}/skill/${slug}`;

  const currentDraft = mode === "generate" ? aiDraft : manualDraft;
  const xPost = currentDraft?.x ?? "";
  const linkedinPost = currentDraft?.linkedin ?? "";
  const showCompose = mode === "manual" && !manualDraft;
  const showLoading = mode === "generate" && !aiDraft && busy;
  // Generation is explicit now (a click, not auto-fired on open) — this is the
  // "ready to generate" state, shown until the user asks for a draft.
  const showGeneratePrompt = mode === "generate" && !aiDraft && !busy;

  function updateXPost(value: string) {
    if (mode === "generate") setAiDraft((d) => (d ? { ...d, x: value } : d));
    else setManualDraft((d) => (d ? { ...d, x: value } : d));
  }

  function updateLinkedinPost(value: string) {
    if (mode === "generate") setAiDraft((d) => (d ? { ...d, linkedin: value } : d));
    else setManualDraft((d) => (d ? { ...d, linkedin: value } : d));
  }

  const [urlCopied, setUrlCopied] = useState(false);
  const [postCopied, setPostCopied] = useState(false);
  const [draftCopied, setDraftCopied] = useState(false);

  // Load any saved social draft the first time the social tab is shown — this just reads
  // a previously-generated draft (no LLM call), so it's fine to do automatically. A *new*
  // draft is never auto-generated any more: that's a real, billed LLM call, so it now only
  // ever runs from an explicit "Generate draft" click (see runGenerate/showGeneratePrompt).
  async function ensureSocialDraft() {
    if (socialLoaded) return;
    setSocialLoaded(true);
    if (!accessToken) {
      setMode("manual");
      return;
    }
    setBusy(true);
    try {
      const draft = await getShareDraft(slug, accessToken);
      if (draft?.linkedin_post) {
        setAiDraft({ x: draft.x_post, linkedin: draft.linkedin_post });
        setSkillUrl(draft.skill_url);
      }
    } catch {}
    setBusy(false);
  }

  function selectTab(next: ShareTab) {
    setTab(next);
    if (next === "social") ensureSocialDraft();
  }

  function openModal() {
    setError(null);
    const initial: ShareTab = canPeople ? "people" : "social";
    setTab(initial);
    setOpen(true);
    if (initial === "social") ensureSocialDraft();
  }

  function close() {
    setOpen(false);
    setError(null);
  }

  async function runGenerate() {
    if (!accessToken) {
      setError("Sign in to generate an AI draft.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const result = await generateShareContent(
        slug,
        { mode: "generate" },
        accessToken,
      );
      setAiDraft({ x: result.x_post, linkedin: result.linkedin_post });
      setSkillUrl(result.skill_url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to generate content");
    } finally {
      setBusy(false);
    }
  }

  async function submitManual() {
    setError(null);
    setBusy(true);
    try {
      const result = await generateShareContent(
        slug,
        { mode: "manual", custom_text: customText },
        accessToken,
      );
      setManualDraft({ x: result.x_post, linkedin: result.linkedin_post });
      setSkillUrl(result.skill_url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to generate content");
    } finally {
      setBusy(false);
    }
  }

  function selectMode(m: Mode) {
    if (m === "generate" && !canGenerate) return; // card is disabled in the UI for this case
    setMode(m);
    setError(null);
  }

  async function copyUrl() {
    await navigator.clipboard.writeText(displayUrl).catch(() => {});
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 1500);
  }

  async function handleShare(platform: "linkedin" | "x") {
    if (platform === "linkedin") {
      try {
        await navigator.clipboard.writeText(linkedinPost);
      } catch {}
      setPostCopied(true);
      setTimeout(() => setPostCopied(false), 4000);
      window.open(
        PLATFORM_META.linkedin.shareUrl(displayUrl),
        "_blank",
        "noopener,noreferrer",
      );
    } else {
      window.open(
        PLATFORM_META.x.shareUrl(xPost),
        "_blank",
        "noopener,noreferrer",
      );
    }
  }

  return (
    <>
      {/* Trigger */}
      <button
        onClick={openModal}
        className={triggerClassName ?? DEFAULT_TRIGGER_CLS}
        style={
          triggerClassName
            ? undefined
            : { borderColor: "var(--brand)", color: "var(--brand)", background: "var(--brand-tint)" }
        }
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13" />
        </svg>
        Share
      </button>

      {/* Overlay */}
      {open && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={close}
        >
          <div
            className="flex w-full max-w-[1066px] flex-col rounded-2xl border border-black/10 bg-white shadow-2xl dark:border-white/10 dark:bg-[#111]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header — identity (title + which skill), then the mode tabs on their own row */}
            <div className="border-b border-black/10 px-7 pt-5 pb-4 dark:border-white/10">
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                    style={{ background: "var(--brand-tint)", color: "var(--brand)" }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                      <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" />
                    </svg>
                  </span>
                  <div className="min-w-0">
                    <h2 className="font-display text-[15px] font-medium leading-tight">Share Skill - {slug}</h2>
                    <p className="mt-0.5 truncate text-xs text-text/50">
                      {tab === "people"
                        ? "Invite collaborators and manage permissions"
                        : "Share it Externally (on LinkedIn or X)"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={close}
                  aria-label="Close"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-text/40 transition hover:bg-black/5 hover:text-text dark:hover:bg-white/10"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {((canPeople && canSocial) || tab === "social") && (
                <div className="mt-4 flex items-center justify-between gap-3">
                  {canPeople && canSocial ? (
                    <div className="inline-flex rounded-lg border border-black/10 bg-black/[0.06] p-0.5 text-sm dark:border-white/10 dark:bg-black/20">
                      {([
                        ["people", "People & Teams"],
                        ["social", "Social media"],
                      ] as [ShareTab, string][]).map(([k, label]) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => selectTab(k)}
                          className={`rounded-md px-3 py-1 font-display font-medium transition ${
                            tab === k ? "bg-primary text-white" : "text-text/55 hover:text-text"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <Eyebrow>Social media</Eyebrow>
                  )}
                </div>
              )}
            </div>

            {/* Body — fixed min-height keeps the modal from resizing when switching tabs */}
            <div className="min-h-[520px]">

            {/* Team & people tab */}
            {tab === "people" && (
              <div className="px-7 py-6">
                <ShareSkill slug={slug} myTeams={myTeams} bare />
              </div>
            )}

            {/* Social media tab — a single view: draft is generated up-front (no
                questionnaire) and is always editable; "Write my own" swaps in a
                compose box instead of asking the user anything. */}
            {tab === "social" && (
              <div className="grid grid-cols-[220px_1fr] divide-x divide-black/8 dark:divide-[#1e1e1e]">
                {/* Left */}
                <div className="flex flex-col gap-5 px-6 py-6">
                  <div>
                    <p className="font-display text-sm font-medium leading-snug">{skill.name}</p>
                  </div>

                  <div>
                    <p className="mb-2"><Eyebrow>Content</Eyebrow></p>
                    <div className="flex flex-col gap-1.5">
                      {(["generate", "manual"] as Mode[]).map((m) => {
                        const disabled = m === "generate" && !canGenerate;
                        return (
                          <button
                            key={m}
                            type="button"
                            disabled={disabled}
                            onClick={() => selectMode(m)}
                            title={disabled ? "Sign in to use AI generation" : undefined}
                            className={`flex items-center gap-3 rounded-xl border px-3 py-2 text-left text-sm transition ${
                              disabled
                                ? "cursor-not-allowed border-black/10 text-text/30 dark:border-white/10"
                                : mode === m
                                  ? "border-primary bg-primary/10 text-primary"
                                  : "border-black/10 text-text/60 hover:border-black/20 hover:text-text dark:border-white/10 dark:hover:border-white/20"
                            }`}
                          >
                            <span
                              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                                mode === m && !disabled ? "border-primary bg-primary" : "border-black/25 dark:border-white/30"
                              }`}
                            >
                              {mode === m && !disabled && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                            </span>
                            <span>
                              <span className="block font-display font-medium">
                                {m === "generate" ? "Generate with AI" : "Write my own"}
                              </span>
                              {disabled && (
                                <span className="block text-xs text-text/40">Sign in to use AI generation</span>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {error && <p className="text-sm text-red-500">{error}</p>}

                  <div className="mt-auto flex flex-col gap-3">
                    <div>
                      <p className="mb-1.5"><Eyebrow>Link</Eyebrow></p>
                      <div className="flex items-start gap-1.5 rounded-lg bg-black/[0.02] px-2.5 py-2 dark:bg-white/[0.02]">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0 text-text/35">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                        </svg>
                        <span className="flex-1 break-all text-[11px] leading-relaxed text-text/50">
                          {displayUrl}
                        </span>
                        <button
                          onClick={copyUrl}
                          title={urlCopied ? "Copied!" : "Copy link"}
                          className="mt-0.5 shrink-0 text-text/35 transition hover:text-text/70 disabled:opacity-40"
                        >
                          {urlCopied ? (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-green-500">
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="11" height="11" rx="2" />
                              <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <p className="mb-2"><Eyebrow>Share on</Eyebrow></p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleShare("linkedin")}
                          disabled={!linkedinPost}
                          title="Copy post & open LinkedIn"
                          className="flex h-9 w-9 items-center justify-center rounded-lg text-white transition hover:opacity-80 disabled:opacity-40"
                          style={{ background: "#0077b5" }}
                        >
                          {PLATFORM_META.linkedin.icon}
                        </button>
                        <button
                          onClick={() => handleShare("x")}
                          disabled={!xPost}
                          title="Open X with post pre-filled"
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-black/15 text-text/70 transition hover:bg-black/5 disabled:opacity-40 dark:border-white/15 dark:hover:bg-white/5"
                        >
                          {PLATFORM_META.x.icon}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right */}
                <div className="flex flex-col gap-3 px-7 py-6">
                  {showCompose ? (
                    <>
                      <p><Eyebrow>Your post <span className="normal-case text-red-400">*</span></Eyebrow></p>
                      <textarea
                        value={customText}
                        onChange={(e) => setCustomText(e.target.value)}
                        placeholder={`Hook: why this skill matters to you\n\nHow others can use it in their Claude workflow…`}
                        rows={12}
                        className="w-full flex-1 resize-none rounded-xl border border-black/15 bg-black/[0.02] px-3 py-2.5 text-sm outline-none transition placeholder:text-text/30 focus:border-primary dark:border-white/15 dark:bg-white/[0.02]"
                      />
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-text/30">{customText.length} chars</p>
                        <button
                          onClick={submitManual}
                          disabled={busy || !customText.trim()}
                          className="rounded-lg px-4 py-2 font-display text-sm font-medium text-white transition disabled:opacity-40"
                          style={{ background: "var(--brand)" }}
                        >
                          {busy ? "Generating…" : "Generate post →"}
                        </button>
                      </div>
                    </>
                  ) : showLoading ? (
                    <div className="flex flex-1 flex-col items-center justify-center gap-3 text-sm text-text/40">
                      <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
                      Generating draft…
                    </div>
                  ) : showGeneratePrompt ? (
                    <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
                      <p className="text-sm text-text/50">AI agent will generate the draft.</p>
                      <button
                        type="button"
                        onClick={runGenerate}
                        disabled={busy}
                        className="rounded-lg px-4 py-2 font-display text-sm font-medium text-white transition disabled:opacity-40"
                        style={{ background: "var(--brand)" }}
                      >
                        {busy ? "Generating…" : "Generate draft →"}
                      </button>
                    </div>
                  ) : (
                    <>
                      {mode === "manual" && (
                        <button
                          onClick={() => setManualDraft(null)}
                          className="self-start font-display text-xs text-text/50 transition hover:text-text"
                        >
                          ← Edit my text
                        </button>
                      )}

                      {postCopied && (
                        <div className="flex items-start gap-3 rounded-xl border border-green-500/25 bg-green-500/8 px-4 py-3 dark:bg-green-500/10">
                          <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-display text-sm font-medium text-green-700 dark:text-green-400">
                              Post copied to clipboard
                            </p>
                            <p className="mt-0.5 text-xs text-green-600/80 dark:text-green-500/80">
                              LinkedIn is opening — click the post box and press{" "}
                              <kbd className="rounded border border-green-500/30 bg-green-500/10 px-1.5 py-0.5 font-mono text-[11px]">⌘V</kbd>
                              {" "}or{" "}
                              <kbd className="rounded border border-green-500/30 bg-green-500/10 px-1.5 py-0.5 font-mono text-[11px]">Ctrl+V</kbd>
                              {" "}to paste.
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-1 rounded-lg border border-black/8 bg-black/[0.02] p-1 dark:border-white/8 dark:bg-white/[0.02]">
                        {(["linkedin", "x"] as ActiveView[]).map((v) => (
                          <button
                            key={v}
                            onClick={() => setActiveView(v)}
                            className={`flex flex-1 items-center justify-center gap-2 rounded-md py-1.5 font-display text-xs font-medium transition ${
                              activeView === v
                                ? "bg-white shadow-sm dark:bg-white/10 text-text"
                                : "text-text/45 hover:text-text/70"
                            }`}
                          >
                            {PLATFORM_META[v].icon}
                            {PLATFORM_META[v].label}
                          </button>
                        ))}
                      </div>

                      <div className="flex items-center justify-between">
                        <Eyebrow>Draft</Eyebrow>
                        <div className="flex items-center gap-3">
                          {activeView === "x" ? (
                            <span
                              className={`text-xs tabular-nums ${xPost.length > X_LIMIT ? "font-semibold text-red-500" : "text-text/30"}`}
                            >
                              {xPost.length} / {X_LIMIT}
                            </span>
                          ) : (
                            <span className="text-xs tabular-nums text-text/30">{linkedinPost.length} chars</span>
                          )}
                          {mode === "generate" && (
                            <button
                              onClick={runGenerate}
                              disabled={busy}
                              title="Generate a new draft"
                              className="flex items-center gap-1 text-text/35 transition hover:text-text/70 disabled:opacity-40"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 4v6h-6M1 20v-6h6" />
                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                              </svg>
                              <span className="font-display text-[11px]">{busy ? "Generating…" : "Regenerate"}</span>
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              await navigator.clipboard
                                .writeText(activeView === "linkedin" ? linkedinPost : xPost)
                                .catch(() => {});
                              setDraftCopied(true);
                              setTimeout(() => setDraftCopied(false), 1500);
                            }}
                            title="Copy draft"
                            className="flex items-center gap-1 text-text/35 transition hover:text-text/70"
                          >
                            {draftCopied ? (
                              <>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-green-500">
                                  <path d="M20 6L9 17l-5-5" />
                                </svg>
                                <span className="font-display text-[11px] text-green-500">Copied</span>
                              </>
                            ) : (
                              <>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="9" y="9" width="11" height="11" rx="2" />
                                  <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                                </svg>
                                <span className="font-display text-[11px]">Copy</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>

                      {activeView === "linkedin" ? (
                        <textarea
                          value={linkedinPost}
                          onChange={(e) => updateLinkedinPost(e.target.value)}
                          rows={13}
                          className="w-full flex-1 resize-none rounded-xl border border-black/15 bg-black/[0.02] px-4 py-3 text-sm leading-relaxed outline-none transition focus:border-primary dark:border-white/15 dark:bg-white/[0.02]"
                        />
                      ) : (
                        <>
                          <textarea
                            value={xPost}
                            onChange={(e) => updateXPost(e.target.value)}
                            rows={5}
                            className={`w-full resize-none rounded-xl border bg-black/[0.02] px-4 py-3 text-sm leading-relaxed outline-none transition focus:border-primary dark:bg-white/[0.02] ${
                              xPost.length > X_LIMIT
                                ? "border-red-400/60"
                                : "border-black/15 dark:border-white/15"
                            }`}
                          />
                          {xPost.length > X_LIMIT && (
                            <p className="text-xs text-red-500">
                              Over the 280-character limit — shorten before sharing.
                            </p>
                          )}
                        </>
                      )}

                      <p className="text-xs text-text/30">
                        Edit freely — changes here only affect what you share, not the skill.
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
